import { useCallback, useEffect, useRef } from "react";

/**
 * Effet bulles élégantes au survol — réutilisable.
 * S'attache à son parent (qui DOIT être en position relative).
 * Suit la souris au pixel près : pas de throttle d'event, juste un guard
 * de distance minimale entre deux spawns pour rester fluide sans décalage.
 */
const DEFAULT_COLORS = [
  "hsl(38 60% 55% / 0.55)", // accent
  "hsl(217 70% 55% / 0.45)", // primary
  "hsl(0 0% 100% / 0.35)", // white
];

interface Props {
  /** Couleurs HSL (avec alpha) à piocher au hasard. */
  colors?: string[];
  /** Distance min en px entre deux spawns successifs (défaut 14). */
  minDistance?: number;
  /** Échelle globale des bulles (1 = défaut). */
  scale?: number;
  /** zIndex de la couche. */
  zIndex?: number;
}

export const HoverBubbles = ({
  colors = DEFAULT_COLORS,
  minDistance = 14,
  scale = 1,
  zIndex = 0,
}: Props) => {
  const layerRef = useRef<HTMLDivElement>(null);
  const last = useRef({ x: -9999, y: -9999 });

  const spawn = useCallback(
    (x: number, y: number) => {
      const layer = layerRef.current;
      if (!layer) return;

      const bubble = document.createElement("span");
      const size = (6 + Math.random() * 16) * scale;
      const color = colors[Math.floor(Math.random() * colors.length)];
      const drift = (Math.random() - 0.5) * 50;
      const rise = 60 + Math.random() * 50;

      bubble.style.cssText = `
        position:absolute;left:0;top:0;width:${size}px;height:${size}px;
        transform:translate(${x - size / 2}px, ${y - size / 2}px) scale(0.4);
        border-radius:9999px;pointer-events:none;
        background:radial-gradient(circle at 30% 30%, rgba(255,255,255,0.75), ${color} 55%, transparent 75%);
        box-shadow:0 0 10px ${color};
        opacity:0;will-change:transform,opacity;
        transition:transform 1.4s cubic-bezier(.16,1,.3,1), opacity 1.4s ease-out;
      `;
      layer.appendChild(bubble);

      // Force layout puis transition vers l'état final
      // (utiliser rAF pour s'assurer que le state initial est appliqué)
      requestAnimationFrame(() => {
        bubble.style.opacity = "1";
        bubble.style.transform = `translate(${x - size / 2 + drift}px, ${
          y - size / 2 - rise
        }px) scale(${0.6 + Math.random() * 0.6})`;
        requestAnimationFrame(() => {
          bubble.style.opacity = "0";
        });
      });

      window.setTimeout(() => bubble.remove(), 1500);
    },
    [colors, scale],
  );

  useEffect(() => {
    const layer = layerRef.current;
    const parent = layer?.parentElement;
    if (!layer || !parent) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const onMove = (e: MouseEvent) => {
      const rect = parent.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const dx = x - last.current.x;
      const dy = y - last.current.y;
      if (dx * dx + dy * dy < minDistance * minDistance) return;
      last.current = { x, y };
      spawn(x, y);
    };

    parent.addEventListener("mousemove", onMove, { passive: true });
    return () => parent.removeEventListener("mousemove", onMove);
  }, [minDistance, spawn]);

  return (
    <div
      ref={layerRef}
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
      style={{ zIndex }}
    />
  );
};
